import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  Flex,
  Separator,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'

import { AwaitingVoltageLegend } from '~/components/balance/AwaitingVoltageMark'
import { DateNav } from '~/components/balance/FilterCard/DateNav'
import { ToggleSwitch } from '~/components/balance/FilterCard/ToggleSwitch'
import {
  DsSelect,
  type DsSelectOption,
  type DsSelectOptions,
  flattenDsSelectOptions,
} from '~/components/common/DsSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import {
  BALANCE_DEMAND_SERIES_COLORS,
  BALANCE_VOLTAGE_CLASS_COLORS,
} from '~/constants/chartPalette'
import type { SeriesKey } from '~/pages/BalanceDemand/constants'
import type { DetailFilterState } from '~/pages/BalanceDemandDetail/constants'
import { isDemandGroupFilter } from '~/pages/BalanceDemandDetail/constants'
import type { BgFilterSelection } from '~/pages/BalanceDemandDetail/hooks/useDemandDetail'
import {
  UNATTRIBUTED_BG_CODE,
  bgOptionLicenseTypeLabel,
  groupBgOptionsByLicenseType,
} from '~/pages/BalanceDemandDetail/models/bgFilterOptions'

type ToggleItem = {
  key: SeriesKey
  labelKey: string
  /** ON トラック色 (design .switch-item[data-color])。省略 = 既定 accent-primary。 */
  color?: string
}

// ラベルは一覧 (balanceDemand.filter) と同一文言のため i18n key を共用する。
// [design差異] design (`balance/bg-region.html` L112-113) は「基本表示」に
// 太陽光計画値/太陽光実績値の switch-item を今も持つが、データソースが
// 未確定・未配線 (#2002) のため #5282 で画面から削除した (doji-bg.md
// Discussion「太陽光計画値/実績値 系列の扱い」)。復活条件は #2002 の解決と
// セット。design 側の改修は未着手 — 本注記が実装漏れ判定の起点。
const BASIC_TOGGLES: ToggleItem[] = [
  {
    key: 'demandPlan',
    labelKey: 'page:balanceDemand.filter.demandPlan',
    color: BALANCE_DEMAND_SERIES_COLORS.demandPlan,
  },
  {
    key: 'demandActual',
    labelKey: 'page:balanceDemand.filter.demandActual',
    color: BALANCE_DEMAND_SERIES_COLORS.demandActual,
  },
  { key: 'weather', labelKey: 'page:balanceDemand.filter.weather' },
]

const ANALYSIS_TOGGLES: ToggleItem[] = [
  {
    key: 'temperature',
    labelKey: 'page:balanceDemand.filter.temperature',
    color: BALANCE_DEMAND_SERIES_COLORS.temperature,
  },
  {
    key: 'precipitation',
    labelKey: 'page:balanceDemand.filter.precipitation',
    color: BALANCE_DEMAND_SERIES_COLORS.precipitation,
  },
  {
    key: 'procurementNet',
    labelKey: 'page:balanceDemand.filter.procurementNet',
    color: BALANCE_DEMAND_SERIES_COLORS.procurementNet,
  },
]

// 電圧階級別は「詳細」配下から独立した親トグルグループ (doji-bg.md AC-BDB-031)。
const VOLTAGE_TOGGLES: ToggleItem[] = [
  {
    key: 'voltageExtraHigh',
    labelKey: 'page:balanceDemand.filter.voltageExtraHigh',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageExtraHigh,
  },
  {
    key: 'voltageHigh',
    labelKey: 'page:balanceDemand.filter.voltageHigh',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageHigh,
  },
  {
    key: 'voltageLow',
    labelKey: 'page:balanceDemand.filter.voltageLow',
    color: BALANCE_VOLTAGE_CLASS_COLORS.voltageLow,
  },
]

const DETAIL_TOGGLES: ToggleItem[] = [
  {
    key: 'demandActualCorrected',
    labelKey: 'page:balanceDemand.filter.demandActualCorrected',
    color: BALANCE_DEMAND_SERIES_COLORS.demandActualCorrected,
  },
  {
    key: 'dewPoint',
    labelKey: 'page:balanceDemand.filter.dewPoint',
    color: BALANCE_DEMAND_SERIES_COLORS.dewPoint,
  },
]

/**
 * 絞り込みがまだ効かないフィルタのラベル (= ラベル + 「準備中」)。
 * read model に需要グループの集計軸が無く、選んでも結果が変わらない
 * (#2002)。操作できるのに何も起きない状態は「絞り込んだつもり」の誤読を生むため、
 * Select は disabled にして理由を短く添える。
 */
const PendingFilterLabel = ({
  label,
  pendingLabel,
}: {
  label: string
  pendingLabel: string
}): React.ReactElement => (
  <Flex alignItems="center" gap="4px">
    <Text as="span" textStyle="label">
      {label}
    </Text>
    <Text as="span" textStyle="caption" color="Text.lowEmphasis">
      {pendingLabel}
    </Text>
  </Flex>
)

type Props = {
  displayDate: Date
  onDateChange: (date: Date) => void
  onToday: () => void
  filter: DetailFilterState
  onFilterChange: (filter: DetailFilterState) => void
  // 選択肢に含まれるかの補正は呼び出し側 (useDemandDetailWithBgSelection) の責務。
  // 必須にしているのは、渡し忘れると黙って「すべて」表示になるため。
  bgFilter: BgFilterSelection
  /**
   * 表示中のコマに低圧未着があるか。 ある時だけ「詳細」 の行の末尾に網掛けの
   * 見本を出す (design `balance/bg-region.html` の `#lv-pending-legend`)。
   */
  hasAwaitingVoltageKoma: boolean
}

/**
 * ラベルと選択欄の 1 組。狭い幅でも組の途中で改行せず、組のまま次の行へ送る
 * (#9882 Des 判断。design `balance/bg-region.html` の `.filter-group`)。
 */
const FilterField = ({
  label,
  children,
}: {
  label: React.ReactNode
  children: React.ReactNode
}): React.ReactElement => (
  <Flex alignItems="center" flexShrink={0} gap="8px">
    {label}
    {children}
  </Flex>
)

/**
 * 系列トグル 4 行の行ラベル列の幅 (design `css/balance.css:31-32`)。
 * 文字数の違う 4 行でトグル群の左端を揃えるためのもの。
 */
const FILTER_LABEL_WIDTH = '104px'

export const FilterCard = ({
  displayDate,
  onDateChange,
  onToday,
  filter,
  onFilterChange,
  bgFilter,
  hasAwaitingVoltageKoma,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const bgSelectOptions: DsSelectOptions = [
    { label: t('page:balanceDemandDetail.filter.bgAll'), value: '' },
    ...groupBgOptionsByLicenseType(bgFilter.options).map((group) => ({
      label: bgOptionLicenseTypeLabel(group.licenseType, t),
      options: group.options.map((option) => ({
        label: option.bgName,
        value: option.bgCode,
      })),
    })),
    // 帰属不明は BG ではないので種別の見出しを付けず、区切り線で BG の一覧と分ける
    // (AC-BDB-025-2、#9882 Des 判断)。bg_options には載らない。
    ...(bgFilter.unattributedSelectable
      ? [
          {
            label: '',
            options: [
              {
                label: t('page:balanceDemandDetail.filter.bgUnattributed'),
                value: UNATTRIBUTED_BG_CODE,
              },
            ],
          },
        ]
      : []),
  ]
  const selectedBgLabel =
    bgFilter.selectedCode === ''
      ? undefined
      : flattenDsSelectOptions(bgSelectOptions).find(
          (option) => option.value === bgFilter.selectedCode,
        )?.label

  const retailOptions: DsSelectOption[] = [
    { label: t('page:balanceDemandDetail.filter.retailAll'), value: '' },
    ...bgFilter.retailers.map((retailer) => ({
      label: retailer.businessName,
      value: retailer.businessCode,
    })),
    ...(bgFilter.isRetailerUnattributedSelectable
      ? [
          {
            label: t('page:balanceDemandDetail.filter.bgUnattributed'),
            value: UNATTRIBUTED_BG_CODE,
          },
        ]
      : []),
  ]

  const demandGroupOptions: DsSelectOption[] = [
    {
      label: t('page:balanceDemandDetail.filter.demandGroupAll'),
      value: 'all',
    },
    {
      label: t('page:balanceDemandDetail.filter.demandGroupIndustrial'),
      value: 'grp_industrial',
    },
    {
      label: t('page:balanceDemandDetail.filter.demandGroupCommercial'),
      value: 'grp_commercial',
    },
    {
      label: t('page:balanceDemandDetail.filter.demandGroupResidential'),
      value: 'grp_residential',
    },
  ]

  const handleBgChange = useCallback(
    (bgCode: string | undefined): void => {
      if (bgCode !== undefined) {
        // 別 BG にも同じ小売が居ても「前の BG の小売」が黙って効き続けないよう戻す。
        onFilterChange({ ...filter, bgCode, retailerCode: '' })
      }
    },
    [filter, onFilterChange],
  )

  const handleRetailChange = useCallback(
    (retailerCode: string | undefined): void => {
      if (retailerCode !== undefined) {
        onFilterChange({ ...filter, retailerCode })
      }
    },
    [filter, onFilterChange],
  )

  const handleDemandGroupChange = useCallback(
    (demandGroup: string | undefined): void => {
      if (demandGroup !== undefined && isDemandGroupFilter(demandGroup)) {
        onFilterChange({ ...filter, demandGroup })
      }
    },
    [filter, onFilterChange],
  )

  const toggleSeries = useCallback(
    (key: SeriesKey): void => {
      onFilterChange({
        ...filter,
        series: { ...filter.series, [key]: !filter.series[key] },
      })
    },
    [filter, onFilterChange],
  )

  const renderToggle = (
    item: ToggleItem,
    isDisabled: boolean,
  ): React.ReactElement => (
    <ToggleSwitch
      key={item.key}
      isDisabled={isDisabled}
      isOn={filter.series[item.key]}
      label={t(item.labelKey)}
      onColor={item.color}
      onToggle={() => toggleSeries(item.key)}
    />
  )

  return (
    <Box
      flexShrink={0}
      /* design `css/balance.css:10` `.filter-card { margin-bottom:2px }`。
         同時同量 4 画面のうち一覧 2 画面は 2px で、 詳細 2 画面だけ 4px に
         割れていた (#7064)。 */
      marginBottom="2px"
      padding="12px"
      borderWidth="1px"
      // design `css/balance.css:10` の `.filter-card` は border-low。
      // 実装は 1 段濃い midEmphasis だった (issue #7064)。
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      backgroundColor="Surface.primary"
    >
      {/* design bg-region の .filter-top と同じ 3 列グリッド
          (minmax(0,1fr) / 区切り線 / minmax(0,1fr))。左を flex:0 1 auto の
          content 幅にすると、絞り込みラベルが 1 語増えるだけで左が横幅を食い、
          右の系列トグル 11 個が 1 行あたり 2 段へ折り返してカードが縦に膨らむ
          (#4515 の「準備中」ラベル追加でこれを踏み、下のチャートが 0px に潰れた)。
          minmax(0,1fr) の等分割なら、左の増減が右の行数に伝播しない。 */}
      <Box
        alignItems="stretch"
        gap="12px"
        gridTemplateColumns="minmax(0, 1fr) auto minmax(0, 1fr)"
        display="grid"
      >
        {/* 左: 表示日 + 絞り込み selects */}
        <Flex
          alignItems="center"
          alignContent="flex-start"
          flexWrap="wrap"
          gap="12px"
        >
          <DateNav
            displayDate={displayDate}
            labelKeyPrefix="page:balanceDemandDetail.filter"
            layout="horizontal"
            onDateChange={onDateChange}
            onToday={onToday}
          />

          <FilterField
            label={
              <Text as="span" textStyle="label">
                {t('page:balanceDemandDetail.filter.bg')}
              </Text>
            }
          >
            {/* 幅 200px は BG の取り違えを避けるため小売より広く取った値で、収まらない
                BG 名は省略して全文を Tooltip と開いた一覧で読む (#9882 Des 判断)。
                素の DS Select は menu を親の中に描くため、 直下の表の sticky header が
                menu の上に重なって選択肢を押せない (F6 で実測)。 body へ portal する
                DsSelect を使う。 */}
            {/* 「すべて」の間は Tooltip を外さず閉じたままにする。HintTooltip のように
                外すと Select が remount され、選んだ直後に focus が外れる。 */}
            <Tooltip
              content={selectedBgLabel}
              open={selectedBgLabel === undefined ? false : undefined}
            >
              <Box>
                <DsSelect
                  width="200px"
                  aria-label={t('page:balanceDemandDetail.filter.bg')}
                  onChange={handleBgChange}
                  options={bgSelectOptions}
                  value={bgFilter.selectedCode}
                />
              </Box>
            </Tooltip>
          </FilterField>

          {/* design (`balance/bg-region.html` L57-67) は表示日の群と小売の群を
              `.filter-group` 2 つの gap だけで分けており、 縦の区切り線を持たない
              (同ファイルの `.filter-divider-v` は L105 の別の位置)。 実装だけが
              ここに区切り線を足していた (2026-09-07 デザイナー指摘)。 */}

          <FilterField
            label={
              <Text as="span" textStyle="label">
                {t('page:balanceDemandDetail.filter.retail')}
              </Text>
            }
          >
            {/* 無効の理由は「BG を選ぶと有効になる」で未実装ではないので準備中ラベルは出さない。 */}
            <DsSelect
              width="160px"
              aria-label={t('page:balanceDemandDetail.filter.retail')}
              isDisabled={!bgFilter.isRetailerSelectEnabled}
              onChange={handleRetailChange}
              options={retailOptions}
              value={bgFilter.selectedRetailerCode}
            />
          </FilterField>

          <FilterField
            label={
              <PendingFilterLabel
                label={t('page:balanceDemandDetail.filter.demandGroup')}
                pendingLabel={t('page:balanceDemandDetail.filter.comingSoon')}
              />
            }
          >
            <DsSelect
              width="160px"
              aria-label={t('page:balanceDemandDetail.filter.demandGroup')}
              isDisabled
              onChange={handleDemandGroupChange}
              options={demandGroupOptions}
              value={filter.demandGroup}
            />
          </FilterField>
        </Flex>

        <Separator orientation="vertical" />

        {/* 右: 系列トグル 4 行 (design bg-region 基本表示/電圧階級別/分析補助/詳細。
            一覧 BalanceDemand の FilterCard と同構成 + 凡例色トラック)。
            行ラベルは `label` (md 14px / 600) — design bg-region.html の
            `.form-label` / その中の `.ecl-checkbox` の実測値 (2026-08-06)。 */}
        <Flex flexDirection="column" gap="8px" minWidth={0}>
          <Flex alignItems="center" flexWrap="wrap" gap="12px">
            {/* 行ラベル列は 104px。 design `css/balance.css:31-32` の
              `.filter-card .form-label` / `.filter-row > .form-label` が
              `width:104px; flex-shrink:0` を持ち、 文字数の違う 4 行で
              トグル群の左端を揃えている。

              **`width` ではなく `minWidth` にする。** design は EN で
              `html[lang="en"] … { width:auto; min-width:104px }` と上書きしており
              (同 :34-35)、 英語ラベルは 104px を超えて伸ばす。 `minWidth` 1 本なら
              JA (全ラベルが 104px 未満) では 104px 固定と同じに揃い、 EN では
              切り落とさず伸びる。 */}
            <Text
              as="span"
              textStyle="label"
              flexShrink={0}
              minWidth={FILTER_LABEL_WIDTH}
              whiteSpace="nowrap"
            >
              {t('page:balanceDemand.filter.basicSeries')}
            </Text>
            {BASIC_TOGGLES.map((item) =>
              renderToggle(
                item,
                item.key === 'demandPlan' && !bgFilter.isDemandPlanEnabled,
              ),
            )}
          </Flex>

          {/* 基本表示行と電圧階級別行の間の水平罫線。 design
              `bg-region.html:116` の `<div class="filter-divider-h">` +
              `css/balance.css:46` の `{ height:1px; width:100%;
              background:var(--border-low) }`。 8080 のコメントは電圧階級別の
              位置を「区切り線の下」 と定義しており、 罫線が「常時表示の基本系列」 と
              「親トグルで束ねる 3 群」 の境になっている。 */}
          <Separator />
          <Flex alignItems="center" flexWrap="wrap" gap="12px">
            <Flex
              alignItems="center"
              flexShrink={0}
              gap="4px"
              minWidth={FILTER_LABEL_WIDTH}
            >
              <Checkbox
                checked={filter.voltageEnabled}
                onCheckedChange={() =>
                  onFilterChange({
                    ...filter,
                    voltageEnabled: !filter.voltageEnabled,
                  })
                }
              >
                <Text as="span" textStyle="label" whiteSpace="nowrap">
                  {t('page:balanceDemand.filter.voltageClass')}
                </Text>
              </Checkbox>
              <HelpIcon
                ariaLabel={t('page:balanceDemand.filter.groupHelp', {
                  group: t('page:balanceDemand.filter.voltageClass'),
                })}
                content={t('page:balanceDemand.filter.voltageClassTooltip')}
              />
            </Flex>
            {VOLTAGE_TOGGLES.map((item) =>
              renderToggle(item, !filter.voltageEnabled),
            )}
          </Flex>
          <Flex alignItems="center" flexWrap="wrap" gap="12px">
            <Flex
              alignItems="center"
              flexShrink={0}
              gap="4px"
              minWidth={FILTER_LABEL_WIDTH}
            >
              <Checkbox
                checked={filter.analysisEnabled}
                onCheckedChange={() =>
                  onFilterChange({
                    ...filter,
                    analysisEnabled: !filter.analysisEnabled,
                  })
                }
              >
                <Text as="span" textStyle="label" whiteSpace="nowrap">
                  {t('page:balanceDemand.filter.analysis')}
                </Text>
              </Checkbox>
              <HelpIcon
                ariaLabel={t('page:balanceDemand.filter.groupHelp', {
                  group: t('page:balanceDemand.filter.analysis'),
                })}
                content={t('page:balanceDemand.filter.analysisTooltip')}
              />
            </Flex>
            {ANALYSIS_TOGGLES.map((item) =>
              renderToggle(
                item,
                !filter.analysisEnabled ||
                  (item.key === 'procurementNet' &&
                    !bgFilter.isProcurementNetEnabled),
              ),
            )}
          </Flex>
          <Flex alignItems="center" flexWrap="wrap" gap="12px">
            <Flex
              alignItems="center"
              flexShrink={0}
              gap="4px"
              minWidth={FILTER_LABEL_WIDTH}
            >
              <Checkbox
                checked={filter.detailEnabled}
                onCheckedChange={() =>
                  onFilterChange({
                    ...filter,
                    detailEnabled: !filter.detailEnabled,
                  })
                }
              >
                <Text as="span" textStyle="label" whiteSpace="nowrap">
                  {t('page:balanceDemand.filter.detail')}
                </Text>
              </Checkbox>
              <HelpIcon
                ariaLabel={t('page:balanceDemand.filter.groupHelp', {
                  group: t('page:balanceDemand.filter.detail'),
                })}
                content={t('page:balanceDemand.filter.detailTooltip')}
              />
            </Flex>
            {DETAIL_TOGGLES.map((item) =>
              renderToggle(item, !filter.detailEnabled),
            )}
            {hasAwaitingVoltageKoma && <AwaitingVoltageLegend />}
          </Flex>
        </Flex>
      </Box>
    </Box>
  )
}
